import Vue from "vue"

import { GetterTree, ActionTree, MutationTree } from "vuex"
import { RootState } from "~/store"

import resetMixin from "~/mixins/store/resetToInitialState"

import UserKind from "~/types/userKind"
export type SubmissionCounter = [number, number, number]
interface SubmissionMetaResponse {
  answered: number
  sent: number
  refused: number
} //This is the exact order of the axios response
type SubmissionMetaResponseKeys = keyof SubmissionMetaResponse

const initialState = () => ({
  email: "",
  first_name: "",
  first_payment_date: "",
  grooviz: 0,
  id: 0,
  is_active: false,
  is_facebook: false,
  is_staff: false,
  is_verified_mail: false,
  lang: "",
  last_name: "",
  picture: "",
  submissions: [0, 0, 0] as SubmissionCounter,
})
export const state = initialState
export type UserState = ReturnType<typeof state>

export const mutations: MutationTree<UserState> = {
  SET(state, data: UserState) {
    let key: keyof UserState

    for (key in data) {
      const ignoreKey = ["band_set", "agency", "influencer"]
      const misMatchTypeKey = ["first_payment_date"]

      if (typeof state[key] === typeof data[key] && !ignoreKey.includes(key))
        Vue.set(state, key, data[key])
      else if (misMatchTypeKey.includes(key) && !ignoreKey.includes(key))
        Vue.set(state, key, data[key])
    }
  },
  SET_GROOVIZ(state, data) {
    state.grooviz = data
  },
  ...resetMixin.mutations,
}

export const actions: ActionTree<UserState, RootState> = {
  async LOAD({ commit, dispatch }): Promise<UserState> {
    const session = await dispatch("GET_SESSION_DATA")

    commit("SET", session)
    return session
  },
  //TODO Store - Add misc.draft.state types
  async LOGOUT({ commit, dispatch, rootState }) {
    //@ts-ignore
    if (rootState.misc.draft.in_flow === true)
      dispatch("misc/draft/SET_DISPLAY", true, { root: true })
    else {
      try {
        await this.$axios.$post("/auth/logout/")
      } catch (err) {
        //@ts-ignore
        this.$sentry.captureException(err)
      }

      await dispatch("influencer/RESET")
      commit("favorites/RESET")
      commit("band_set/RESET")
      dispatch("draft/RESET", null, { root: true })
      dispatch("cart/RESET", null, { root: true })
      dispatch("band/RESET")
      dispatch("misc/draft/RESET", null, { root: true })
      await dispatch("agency/LOGOUT")
      commit("RESET", initialState())
      Vue.nextTick(() => {
        this.$router.push({ path: "/" })
      })
    }
  },
  async GET_SESSION_DATA({ dispatch }): Promise<UserState> {
    const [user, submissions, grooviz] = await Promise.all([
      dispatch("GET_USER"),
      dispatch("GET_USER_SUB_COUNT"),
      dispatch("GET_USER_GROOVIZ"),
      dispatch("influencer/FETCH"),
      dispatch("agency/FETCH"),
      dispatch("band_set/FETCH"),
      dispatch("band/FETCH"),
    ])

    return { ...user, submissions, grooviz }
  },
  SET_GROOVIZ({ commit }, grooviz): void {
    commit("SET_GROOVIZ", grooviz)
  },
  async GET_USER(): Promise<UserState | null> {
    try {
      return await this.$axios.$get("/auth/user/")
    } catch (err) {
      return null
    }
  },
  async GET_USER_SUB_COUNT(): Promise<SubmissionCounter> {
    return new Promise((resolve) => {
      this.$axios
        .$get("/submission/submission/meta/")
        .then((response: SubmissionMetaResponse) => {
          const keys = Object.keys(response) as SubmissionMetaResponseKeys[]

          resolve(
            keys.reduce(
              (
                accumulator: SubmissionCounter,
                key: SubmissionMetaResponseKeys,
                index: number
              ) => {
                accumulator[index] = response[key]
                return accumulator
              },
              [0, 0, 0]
            )
          )
        })
        .catch(() => {
          resolve([0, 0, 0])
        })
    })
  },
  async GET_USER_GROOVIZ(): Promise<number> {
    return new Promise((resolve) => {
      this.$axios
        .$get("/wallet/wallet/self/")
        .then(({ groovies }: { groovies: number }) => {
          resolve(Number(groovies) || 0)
        })
        .catch(() => {
          resolve(0)
        })
    })
  },
}

export const getters: GetterTree<UserState, RootState> = {
  KIND(state): UserKind {
    if (
      //TODO Store - Add Agency state ? I guess these value should be getters !
      //@ts-ignore
      state.agency.id > 0 &&
      //@ts-ignore
      state.band_set.length > 0
    )
      return "band"
    //@ts-ignore
    else if (state.influencer !== null && state.influencer.id > 0)
      return "influencer"
    else return null
  },
  PICTURE(_, getters): string | undefined {
    if (getters.IS_BAND) return getters["band/GET_PICTURE"]
    if (getters.IS_INF)
      return getters["influencer/GET_PICTURE"]({
        size: "400_400",
        target: "profile_picture",
      })
  },
  IS_UNVALID_BAND(state, getters) {
    return (
      getters.IS_LOGGED_IN &&
      !getters.IS_INF &&
      !getters.IS_BAND &&
      state.last_name.length === 0
    )
  },
  IS_BAND(_, getters): boolean {
    return getters.IS_LOGGED_IN && getters.KIND === "band"
  },
  IS_INF(_, getters): boolean {
    return getters.IS_LOGGED_IN && getters.KIND === "influencer"
  },
  HAS_MULTI_BAND(state, getters): boolean {
    //@ts-ignore
    return getters.IS_BAND && getters.IS_AGENCY && state.band_set.length > 1
  },
  IS_AGENCY(state, getters) {
    //@ts-ignore
    if (getters.KIND === "band") return state.agency.kind === "Pr"
    else return false
  },
  IS_LOGGED_IN(state): boolean {
    return state.id > 0
  },
  IS_PREMIUM(state, getters) {
    //@ts-ignore
    return getters.IS_BAND && state.agency.is_premium
  },
  IS_NEW(state, getters) {
    return getters.IS_BAND && !state.submissions.some((el) => el > 0)
  },
  IS_ADMIN(state) {
    return state.is_staff
  },
}
